/**
 * useUserPreferences — reads and writes per-user KPI configuration.
 *
 * Persistence strategy (in priority order):
 *   1. API  (GET /api/profile/me, PUT /api/profile/me/preferences) — source of truth
 *   2. localStorage — used as an offline/dev fallback so saves survive without a backend
 *
 * Behaviour:
 *   - On mount: load from localStorage immediately (instant, no flash), then
 *     overwrite with API response when it arrives.
 *   - On save: write to localStorage first (always succeeds), then attempt the API.
 *     If the API fails the save still sticks locally; no revert.
 *
 * Key mapping:
 *   Frontend uses snake_case (kpi_config), backend uses camelCase (kpiConfig).
 *   Conversion happens at the API boundary only — internal state stays snake_case.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../services/apiClient';

export const DEFAULT_KPI_SELECTION = [
  'staffing.coverage',
  'quality.task_completion',
  'access.same_day',
  'quality.open_compliance',
  'consumer.satisfaction',
  'finance.denials',
];
const LS_KEY = (sub) => `nn_user_prefs_${sub || 'guest'}`;

/** Convert backend camelCase preferences to frontend snake_case */
function fromApi(apiPrefs) {
  if (!apiPrefs) return null;
  const out = {};
  if (apiPrefs.kpiConfig) out.kpi_config = apiPrefs.kpiConfig;
  return Object.keys(out).length > 0 ? out : null;
}

/** Convert frontend snake_case preferences to backend camelCase */
function toApi(prefs) {
  const out = {};
  if (prefs.kpi_config) out.kpiConfig = prefs.kpi_config;
  return out;
}

function loadFromStorage(sub) {
  try {
    const raw = localStorage.getItem(LS_KEY(sub));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(sub, prefs) {
  try {
    localStorage.setItem(LS_KEY(sub), JSON.stringify(prefs));
  } catch {
    // storage quota exceeded — ignore
  }
}

export function useUserPreferences() {
  const { user, accessToken } = useAuth();
  const sub = user?.attributes?.sub;

  const [prefs, setPrefs] = useState(() => {
    const stored = loadFromStorage(sub);
    return stored ?? { kpi_config: { selected: DEFAULT_KPI_SELECTION } };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Re-load from localStorage when the user identity becomes known
  useEffect(() => {
    if (!sub) { setLoading(false); return; }
    const stored = loadFromStorage(sub);
    if (stored) setPrefs(stored);
  }, [sub]);

  // Fetch from API (profile endpoint); overrides localStorage if successful
  useEffect(() => {
    if (!sub || !accessToken) { setLoading(false); return; }

    let cancelled = false;
    fetch(`${API_BASE}/api/profile/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((profile) => {
        if (!cancelled) {
          const apiPrefs = fromApi(profile.preferences);
          if (apiPrefs) {
            const merged = { ...prefs, ...apiPrefs };
            setPrefs(merged);
            saveToStorage(sub, merged);
          }
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, accessToken]);

  const savePreferences = async (newPrefs) => {
    const merged = { ...prefs, ...newPrefs };

    // Always persist locally first — this is what the UI reads from
    setPrefs(merged);
    saveToStorage(sub, merged);
    setError(null);

    // Best-effort API sync; failure does not revert the local state
    try {
      const res = await fetch(`${API_BASE}/api/profile/me/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(toApi(newPrefs)),
      });
      if (!res.ok) throw new Error('save failed');
      const profile = await res.json();
      const apiPrefs = fromApi(profile.preferences);
      if (apiPrefs) {
        const synced = { ...merged, ...apiPrefs };
        setPrefs(synced);
        saveToStorage(sub, synced);
      }
    } catch {
      // API unavailable — localStorage save above is sufficient
    }
  };

  return { prefs, loading, error, savePreferences };
}
